/**
 * Formats numeric phone-number digit blocks for the announcement editor.
 *
 * This follows the Java NumberFormatService semantics rather than every quirk
 * of prettyprintNumber.php: blocks use single spaces, area codes use
 * longest-prefix matching, international input determines its own lkz, and
 * Austria uses its own area-code table.
 */

const COUNTRY_CODES = ['351', '352', '31', '32', '33', '34', '39', '41', '43', '44', '49', '1'];

export function parseAreaCodeCsv(csv: string): string[] {
    return (csv || '').split(/\r?\n/)
        .map((line: string) => line.trim())
        .filter((line: string) => /^[0-9]+$/.test(line));
}

export function deriveDefaultLkz(mobileNumber: string): string | null {
    if (!mobileNumber) {
        return null;
    }
    let digits = mobileNumber.trim().replace(/[^0-9+]/g, '');
    if (digits.indexOf('+') === 0) {
        digits = digits.substring(1);
    } else if (digits.indexOf('00') === 0) {
        digits = digits.substring(2);
    }
    if (!digits) {
        return null;
    }
    return digits.indexOf('1') === 0 ? '1' : digits.length >= 2 ? digits.substring(0, 2) : null;
}

export function resolveLkz(rawNumber: string, defaultLkz: string | null): string | null {
    if (rawNumber.indexOf('+') === 0 || rawNumber.indexOf('00') === 0) {
        const international = rawNumber.indexOf('+') === 0 ? rawNumber.substring(1) : rawNumber.substring(2);
        return longestPrefix(international, COUNTRY_CODES);
    }
    return defaultLkz;
}

export function formatPhoneNumber(rawNumber: string, defaultLkz: string | null, areaCodes: string[] = []): string {
    if (rawNumber === null || rawNumber === undefined) {
        return rawNumber;
    }
    const input = rawNumber.trim();
    if (!/^[0-9+]+$/.test(input)) {
        return rawNumber;
    }

    const lkz = resolveLkz(input, defaultLkz);
    let national = input;
    if (input.indexOf('+') === 0 || input.indexOf('00') === 0) {
        const international = input.indexOf('+') === 0 ? input.substring(1) : input.substring(2);
        const countryCode = lkz;
        national = countryCode
            ? countryCode === '1' ? international.substring(countryCode.length) : '0' + international.substring(countryCode.length)
            : international;
    }

    const areaCode = lkz === null ? null : longestPrefix(national, areaCodes || []);
    if (areaCode) {
        return (areaCode + ' ' + groupDigits(national.substring(areaCode.length), lkz === '1')).trim();
    }
    return groupDigits(national, lkz === '1');
}

function longestPrefix(value: string, candidates: string[]): string | null {
    let longest: string | null = null;
    for (const candidate of candidates) {
        if (candidate && value.lastIndexOf(candidate, 0) === 0 &&
            (!longest || candidate.length > longest.length)) {
            longest = candidate;
        }
    }
    return longest;
}

function groupDigits(number: string, keepTrailingFour: boolean): string {
    if (!number) {
        return '';
    }
    const blocks: string[] = [];
    let rest = number;
    while (rest.length > 0) {
        if (rest.length === 4) {
            if (keepTrailingFour) {
                blocks.push(rest);
            } else {
                blocks.push(rest.substring(0, 2), rest.substring(2));
            }
            break;
        }
        const take = Math.min(3, rest.length);
        blocks.push(rest.substring(0, take));
        rest = rest.substring(take);
    }
    return blocks.join(' ');
}
